// Third Party Imports
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {CSSTransition} from 'react-transition-group';

// Primary Imports
import InlineMessageStateless from './InlineMessageStateless';
import {
    MESSAGE_TYPE,
    TRANSITION_STATE,
    SETTINGS,
    inlineMessageUtil
} from './utils/inlineMessageUtil';

// styles
import './InlineMessage.less';

class InlineMessage extends Component {
    static propTypes = {
        /** flag to animate if the type is the same but the message is different */
        animateIfJustMessageChanges: PropTypes.bool,
        /** The aria-live value for the alert.
         * 'polite' - indicates that the screen reader should wait until the user is idle before presenting updates to the user
         * 'assertive' may interupt user's flow
         */
        ariaLive: PropTypes.oneOf(['assertive', 'polite']),
        /** The message's id, required for a11y */
        id: PropTypes.string.isRequired,
        /** Flag to make message text use white text for better a11y contrast when used on dark backgrounds */
        inverse: PropTypes.bool,
        /** Unique identifier for a mounted instance of the component; to mount a new instance (eg. to trigger a new shake), pass a new key to the component */
        key: PropTypes.oneOf(['number', 'string']),
        /** The bottom margin. This is used to write the inline-style to enable the
         * transistion of this value (if maxLineCount > 0)*/
        marginBottom: PropTypes.number,
        /** The top margin. This is used to write the inline-style to enable the
         * transistion of this value (if maxLineCount > 0)*/
        marginTop: PropTypes.number,
        /** Max line count for expand/collapse transition. This is used to transition the max-height
         * css property of the component when entering or leaving the collapsed (aka 'none') state.
         * Setting this value to 0 makes this height transition immediate, and will accomodate
         * a message with any number of lines. When greater than 0, this value should always be
         * greater than or equal to the actual line count. The default value of 3, works for 1,
         * 2, and 3 lines messages with the only minor difference being the effective transition
         * duration and delay per line. Choose 6 if you have 4-6 lines, and if you have a message longer
         * than 6 lines, you probably need something other than the inline message. */
        maxLineCount: PropTypes.oneOf([0, 3, 6]),
        /** The content of the message */
        message: PropTypes.node,
        /** flag to disable all animation */
        noAnimation: PropTypes.bool,
        /** Should the message shake; should be passed in conjunction with a new key value to reset the component. */
        shake: PropTypes.bool,
        /** The type of message */
        type: PropTypes.oneOf(['help', 'info', 'warn', 'danger', 'success'])
    };

    static defaultProps = {
        animateIfJustMessageChanges: true,
        ariaLive: 'polite',
        inverse: false,
        marginBottom: 0,
        marginTop: 0,
        maxLineCount: 3,
        message: '',
        noAnimation: false,
        shake: false,
        type: 'help'
    };

    constructor(props) {
        super(props);

        const {
            marginBottom,
            marginTop,
            maxLineCount,
            message,
            noAnimation,
            type
        } = this.props;

        const adjType = inlineMessageUtil.getAdjustedType(message, type);

        // since this is based on props, it can be computed now
        const enterExitStyles = inlineMessageUtil.generateEnterExitStyles({
            mTop: marginTop,
            mBottom: marginBottom,
            maxLineCount,
            noAnimation
        });

        const transitionState =
            adjType === MESSAGE_TYPE.NONE
                ? TRANSITION_STATE.EXITED
                : TRANSITION_STATE.ENTERED;

        this.state = {
            animate: false,
            animationCounter: 0,
            animationType: {},
            enterExitStyles,
            message,
            prevMessage: message,
            prevType: adjType,
            shake: false,
            transitionState,
            type: adjType
        };

        // CSSTransition state change callbacks
        this.onEnter = this.onTransitionStateChange.bind(
            this,
            TRANSITION_STATE.ENTER
        );
        this.onEntering = this.onTransitionStateChange.bind(
            this,
            TRANSITION_STATE.ENTERING
        );
        this.onEntered = this.onTransitionStateChange.bind(
            this,
            TRANSITION_STATE.ENTERED
        );
        this.onExit = this.onTransitionStateChange.bind(
            this,
            TRANSITION_STATE.EXIT
        );
        this.onExiting = this.onTransitionStateChange.bind(
            this,
            TRANSITION_STATE.EXITING
        );
        this.onExited = this.onTransitionStateChange.bind(
            this,
            TRANSITION_STATE.EXITED
        );
    }

    componentDidMount() {
        // only trigger a new shake as the result of a mount,
        // ie. consumer should update "key" prop on <InlineMessage/>
        const {shake} = this.props;

        if (shake) {
            this.setState({
                shake,
                animationCounter: this.state.animationCounter + 1
            });
        }
    }

    // eslint-disable-next-line complexity
    componentDidUpdate(prevProps) {
        const {
            animateIfJustMessageChanges,
            marginBottom,
            marginTop,
            maxLineCount,
            message,
            noAnimation,
            type
        } = this.props;

        const {animationCounter} = this.state;

        // handle triggering animation as a result of type or message changing.
        // since they are the result of a comparison between prevProps and this.props,
        // they should be triggered from componentDidUpdate because
        // prevProps is not available on componentDidMount or deriveStateFromProps
        const typeChanged = prevProps.type !== type;
        const messageChanged = prevProps.message !== message;

        if (typeChanged || messageChanged) {
            const adjType = inlineMessageUtil.getAdjustedType(message, type);
            const adjPrevType = inlineMessageUtil.getAdjustedType(
                prevProps.message,
                prevProps.type
            );
            const animate =
                typeChanged || (messageChanged && animateIfJustMessageChanges);
            const animationType = inlineMessageUtil.computeAnimationType({
                type: adjType,
                prevType: adjPrevType,
                noAnimation
            });

            this.setState({
                animate,
                animationCounter: animate
                    ? animationCounter + 1
                    : animationCounter,
                animationType,
                message,
                prevMessage: prevProps.message,
                prevType: adjPrevType,
                shake: false, // only allow shake on mount; otherwise, set to false
                type: adjType
            });
        }

        // enterExitStyles are calculated in the constructor
        // and then again only if one of these props change:
        // marginTop, marginBottom, maxLineCount, noAnimation
        if (
            this.didChange(prevProps, this.props, [
                'marginTop',
                'marginBottom',
                'maxLineCount',
                'noAnimation'
            ])
        ) {
            const enterExitStyles = inlineMessageUtil.generateEnterExitStyles({
                mTop: marginTop,
                mBottom: marginBottom,
                maxLineCount,
                noAnimation
            });

            this.setState({enterExitStyles});
        }
    }

    didChange(obj1, obj2, keyArray) {
        return keyArray.some((key) => {
            return obj1[key] !== obj2[key];
        });
    }

    onTransitionStateChange(transitionState) {
        if (this.state.transitionState !== transitionState) {
            this.setState({transitionState});
        }
    }

    render() {
        const {
            animate,
            animationCounter,
            animationType,
            message,
            prevMessage,
            prevType,
            shake,
            transitionState,
            type
        } = this.state;

        const {inverse, noAnimation, id, className, ariaLive} = this.props;

        // assign the correct margin, maxheight, etc based on whether component is entering or exiting
        const enterExitStyles =
            this.state.enterExitStyles[transitionState] || {};

        const rootClassNames = classnames('InlineMessage', {
            [className]: !!className
        });

        return (
            <CSSTransition
                classNames="InlineMessage-CSSTransition"
                timeout={noAnimation ? 0 : SETTINGS.TRANSITION_DURATION}
                in={type !== MESSAGE_TYPE.NONE}
                mountOnEnter
                unmountOnExit
                onEnter={this.onEnter}
                onEntering={this.onEntering}
                onEntered={this.onEntered}
                onExit={this.onExit}
                onExiting={this.onExiting}
                onExited={this.onExited}
            >
                <div className={rootClassNames} style={enterExitStyles}>
                    <InlineMessageStateless
                        animate={animate}
                        animationType={animationType}
                        ariaLive={ariaLive}
                        id={id}
                        inverse={inverse}
                        key={animationCounter}
                        message={message}
                        prevMessage={prevMessage}
                        prevType={prevType}
                        type={type}
                        shake={shake}
                        shakeTimeoutCallback={this.props.shakeTimeoutCallback}
                    />
                </div>
            </CSSTransition>
        );
    }
}

export default InlineMessage;
